/**
 * Auth Zustand store — persists to localStorage.
 *
 * State: user, token, refreshToken, isAuthenticated
 * Actions: login, logout, setUser, refreshAuth, refreshCurrentUser
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { User, LoginCredentials, LoginResponse } from "@/types";
import { assertCurrentSession, getSession, onSessionChange, startNewSession } from "@/services/sessionLifecycle";
import { useToastStore } from "./toastStore";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

type RefreshSessionResponse = {
  accessToken: string;
  refreshToken?: string;
  user?: User;
};

let refreshPromise: { session: ReturnType<typeof getSession>; promise: Promise<boolean> } | null = null;
let logoutPromise: Promise<void> = Promise.resolve();

onSessionChange(() => {
  if (refreshPromise?.session !== getSession()) refreshPromise = null;
  useToastStore.setState({ toasts: [] });
});

function authHeaders(token: string | null) {
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

function isDifferentUserSession(current: User | null, next: User) {
  return current?.id !== next.id || current.role !== next.role;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  /** Authenticate with username + password. Returns mustChangePassword flag. */
  login: (credentials: LoginCredentials) => Promise<{ mustChangePassword: boolean }>;
  /** Clear all auth state and call the server logout. */
  logout: () => void;
  /** Patch the current user in place (e.g. after password change). */
  setUser: (user: User) => void;
  /** Attempt to refresh the current session. */
  refreshAuth: () => Promise<boolean>;
  /** Fetch the current user for a persisted/cookie session. */
  refreshCurrentUser: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,

        login: async (credentials: LoginCredentials) => {
          const session = startNewSession(() => {
            set({ user: null, token: null, refreshToken: null, isAuthenticated: false });
          });
          // Do not let a late logout response clear the next login's cookie.
          await logoutPromise;
          assertCurrentSession(session);
          const resp = await fetch(`${BASE_URL}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(credentials),
            credentials: "include",
            signal: session.controller.signal,
          });
          assertCurrentSession(session);

          if (!resp.ok) {
            const data = await resp.json().catch(() => ({}));
            throw new Error(data.error ?? "Přihlášení se nezdařilo");
          }

          const data: LoginResponse = await resp.json();
          assertCurrentSession(session);

          startNewSession(() => set({
            user: data.user,
            token: data.accessToken,
            refreshToken: data.refreshToken ?? null,
            isAuthenticated: true,
          }));

          return { mustChangePassword: data.user?.mustChangePassword ?? false };
        },

        logout: () => {
          startNewSession(() => set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          }));
          const request = fetch(`${BASE_URL}/api/auth/logout`, {
            method: "POST",
            credentials: "include",
            signal: AbortSignal.timeout(10_000),
          }).then(() => {}).catch(() => {});
          logoutPromise = Promise.all([logoutPromise, request]).then(() => {});
        },

        setUser: (user) => set({ user }),

        refreshAuth: () => {
          const session = getSession();
          if (refreshPromise?.session === session) return refreshPromise.promise;
          const { refreshToken: rt } = get();
          const promise = (async () => {
            try {
              const resp = await fetch(`${BASE_URL}/api/auth/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: rt ? JSON.stringify({ refreshToken: rt }) : "{}",
                credentials: "include",
                signal: session.controller.signal,
              });
              if (getSession() !== session) return false;

              if (!resp.ok) {
                get().logout();
                return false;
              }

              const data = (await resp.json()) as RefreshSessionResponse;
              if (getSession() !== session) return false;
              const user = data.user ?? get().user;
              const changedIdentity = user && isDifferentUserSession(get().user, user);
              const publish = () => set({
                token: data.accessToken,
                refreshToken: data.refreshToken ?? (changedIdentity ? null : rt),
                user,
                isAuthenticated: true,
              });
              if (changedIdentity) startNewSession(publish);
              else publish();
              return true;
            } catch {
              if (getSession() === session) get().logout();
              return false;
            } finally {
              if (refreshPromise?.session === session) refreshPromise = null;
            }
          })();
          refreshPromise = { session, promise };
          return promise;
        },

        refreshCurrentUser: async () => {
          const session = getSession();
          const fetchCurrentUser = (token: string | null) =>
            fetch(`${BASE_URL}/api/auth/me`, {
              method: "GET",
              headers: authHeaders(token),
              credentials: "include",
              signal: session.controller.signal,
            });

          try {
            let resp = await fetchCurrentUser(get().token);
            if (getSession() !== session) return false;
            if (resp.status === 401) {
              const refreshed = await get().refreshAuth();
              if (!refreshed || getSession() !== session) return false;
              resp = await fetchCurrentUser(get().token);
            }
            if (getSession() !== session) return false;

            if (!resp.ok) {
              get().logout();
              return false;
            }

            const user = (await resp.json()) as User;
            if (getSession() !== session) return false;
            if (!user?.role) {
              get().logout();
              return false;
            }

            const publish = () => set({ user, isAuthenticated: true });
            if (isDifferentUserSession(get().user, user)) startNewSession(publish);
            else publish();
            return true;
          } catch {
            if (getSession() === session) get().logout();
            return false;
          }
        },
      }),
      {
        name: "stavby-auth",
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
        }),
      },
    ),
    { name: "AuthStore" },
  ),
);
